import { create } from 'zustand';

type TimeRange = '7d' | '30d' | 'custom';
type SortByField = 'publishedAt' | 'reach' | 'impressions' | 'engagement' | 'engagementRate' | 'likes' | 'comments' | 'shares';
type SortOrder = 'asc' | 'desc';
type CompareMode = 'none' | 'platform' | 'period' | 'channel';

interface AnalyticsState {
  // Filters
  timeRange: TimeRange;
  customFrom: string | null; // ISO date string
  customTo: string | null;
  platform: string | null;
  channelId: number | null;

  // Sort
  sortBy: SortByField;
  sortOrder: SortOrder;

  // Pagination
  page: number;
  limit: number;

  // Drawer
  selectedChannelInstanceId: number | null;

  // Compare
  compareMode: CompareMode;

  // Actions
  setTimeRange: (range: TimeRange) => void;
  setCustomDateRange: (from: string, to: string) => void;
  setPlatform: (platform: string | null) => void;
  setChannelId: (channelId: number | null) => void;
  toggleSort: (column: SortByField) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSelectedChannelInstanceId: (id: number | null) => void;
  setCompareMode: (mode: CompareMode) => void;
  resetFilters: () => void;

  // Computed
  getDateRange: () => { from: string; to: string };
}

const getDefaultDateRange = (range: TimeRange, customFrom: string | null, customTo: string | null) => {
  const now = new Date();
  const to = now.toISOString();

  if (range === 'custom' && customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }

  const from = new Date();
  if (range === '30d') {
    from.setDate(from.getDate() - 30);
  } else {
    from.setDate(from.getDate() - 7);
  }

  return { from: from.toISOString(), to };
};

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  // Default state
  timeRange: '30d',
  customFrom: null,
  customTo: null,
  platform: null,
  channelId: null,
  sortBy: 'publishedAt',
  sortOrder: 'desc',
  page: 1,
  limit: 20,
  selectedChannelInstanceId: null,
  compareMode: 'none',

  // Actions
  setTimeRange: (range) => set({ timeRange: range, page: 1 }),
  setCustomDateRange: (from, to) => set({ customFrom: from, customTo: to, timeRange: 'custom', page: 1 }),
  // A channel belongs to one platform: changing the platform drops the channel pick, otherwise the
  // select shows "All Channels" while the request still carries the old channelId (empty results).
  setPlatform: (platform) => set({ platform, channelId: null, page: 1 }),
  setChannelId: (channelId) => set({ channelId, page: 1 }),
  toggleSort: (column) => {
    const state = get();
    if (state.sortBy === column) {
      set({ sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      set({ sortBy: column, sortOrder: 'desc' });
    }
  },
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit, page: 1 }),
  setSelectedChannelInstanceId: (id) => set({ selectedChannelInstanceId: id }),
  setCompareMode: (mode) => set({ compareMode: mode }),
  resetFilters: () => set({
    timeRange: '30d',
    customFrom: null,
    customTo: null,
    platform: null,
    channelId: null,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
    page: 1,
    limit: 20,
    compareMode: 'none',
  }),

  // Computed
  getDateRange: () => {
    const state = get();
    return getDefaultDateRange(state.timeRange, state.customFrom, state.customTo);
  },
}));
